import { useEffect, useRef, useState } from 'react';
import { useCharacterStore } from '@/state/characterStore';
import { useBuffStore, getActiveMultiplier } from '@/state/buffStore';
import { CLASS_COLORS } from '@/data/assetManifest';
import { BuffBar } from './BuffBar';

const HP_FRAME = '/assets/ui/hud/hp_frame.png';
const MP_FRAME = '/assets/ui/hud/mp_frame.png';
const EXP_FRAME = '/assets/ui/hud/exp_frame.png';
const PORTRAIT_FRAME = '/assets/ui/hud/portrait_frame.png';

// Inner-slot insets measured directly from each cropped sprite.
const HP_INSET = { left: 9.83, right: 8.12, top: 25, bottom: 25 };
const MP_INSET = { left: 9.83, right: 5.13, top: 21.88, bottom: 21.88 };
const EXP_INSET = { left: 10.26, right: 3.85, top: 25, bottom: 25 };

interface FramedBarProps {
  pct: number;
  fill: string;
  label: string;
  frameSrc: string;
  width: number | string;
  height: number;
  fontSize: number;
  inset: { left: number; right: number; top: number; bottom: number };
}

function FramedBar({
  pct,
  fill,
  label,
  frameSrc,
  width,
  height,
  fontSize,
  inset,
}: FramedBarProps) {
  const clamped = Math.max(0, Math.min(1, pct));
  return (
    <div style={{ position: 'relative', width, height }}>
      <div
        style={{
          position: 'absolute',
          left: `${inset.left}%`,
          right: `${inset.right}%`,
          top: `${inset.top}%`,
          bottom: `${inset.bottom}%`,
          background: '#0a0a10',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.85)',
        }}
      >
        <div
          style={{
            width: `${clamped * 100}%`,
            height: '100%',
            background: fill,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
            transition: 'width 120ms linear',
            willChange: 'width',
          }}
        />
      </div>
      <img
        src={frameSrc}
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: `${inset.left}%`,
          right: `${inset.right}%`,
          top: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize,
          fontWeight: 700,
          color: '#ffe8b3',
          textShadow: '0 1px 2px #000, 0 0 3px #000',
          letterSpacing: 0.4,
          fontFamily: '"Philosopher", "Marcellus", serif',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
    </div>
  );
}

const CLASS_ICON: Record<'warrior' | 'wizard' | 'archer', string> = {
  warrior: '/assets/skills/warrior/warrior_crest.png',
  wizard: '/assets/skills/wizard/wizard_crest.png',
  archer: '/assets/skills/archer/archer_crest.png',
};

const CLASS_LABEL: Record<'warrior' | 'wizard' | 'archer', string> = {
  warrior: 'Warrior',
  wizard: 'Wizard',
  archer: 'Archer',
};

function toCss(color: number) {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function useTweened(target: number, speedPerSec = 8): number {
  const [display, setDisplay] = useState(target);
  const displayRef = useRef(target);
  const targetRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  useEffect(() => {
    targetRef.current = target;
    if (rafRef.current != null) return;
    const tick = (ts: number) => {
      const last = lastTsRef.current ?? ts;
      const dt = Math.min(0.1, (ts - last) / 1000);
      lastTsRef.current = ts;
      const cur = displayRef.current;
      const tgt = targetRef.current;
      const diff = tgt - cur;
      if (Math.abs(diff) < 0.5) {
        displayRef.current = tgt;
        setDisplay(tgt);
        rafRef.current = null;
        lastTsRef.current = null;
        return;
      }
      const next = cur + diff * (1 - Math.exp(-speedPerSec * dt));
      displayRef.current = next;
      setDisplay(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [target, speedPerSec]);

  return display;
}

export function VitalsHUD() {
  const ch = useCharacterStore((s) => s.current);
  const hp = useCharacterStore((s) => s.hp);
  const mp = useCharacterStore((s) => s.mp);
  const maxHp = useCharacterStore((s) => s.maxHp);
  const maxMp = useCharacterStore((s) => s.maxMp);
  const exp = useCharacterStore((s) => s.exp);
  const expToLevel = useCharacterStore((s) => s.expToLevel);
  const buffs = useBuffStore((s) => s.buffs);

  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const expMul = getActiveMultiplier(buffs, 'exp');

  const safeMaxHp = Math.max(1, maxHp);
  const safeMaxMp = Math.max(1, maxMp);
  const safeMaxExp = Math.max(1, expToLevel);

  const hpDisplay = useTweened(hp, 10);
  const mpDisplay = useTweened(mp, 10);
  const expDisplay = useTweened(exp, 6);

  const klass = ch?.class ?? 'warrior';
  const accent = toCss(CLASS_COLORS[klass]);
  const level = ch?.level ?? 1;
  const resetCount = ch?.resetCount ?? 0;

  const PORTRAIT_SIZE = 64;
  const BAR_W = 240;
  const BAR_H = 22;
  const HP_FONT = 13;
  const MP_FONT = 11;
  const EXP_FONT = 11;

  return (
    <div
      style={{
        position: 'absolute',
        left: 12,
        top: 8,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.6))',
      }}
    >
      {/* Portrait */}
      <div
        style={{
          position: 'relative',
          width: PORTRAIT_SIZE,
          height: PORTRAIT_SIZE,
          flexShrink: 0,
        }}
        title={CLASS_LABEL[klass]}
      >
        <div
          style={{
            position: 'absolute',
            inset: '14%',
            background: `radial-gradient(circle at 35% 30%, ${accent}, #0b1220 80%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `inset 0 0 8px rgba(0,0,0,0.6), 0 0 6px ${accent}80`,
            overflow: 'hidden',
          }}
        >
          <img
            src={CLASS_ICON[klass]}
            alt={CLASS_LABEL[klass]}
            draggable={false}
            style={{
              width: '88%',
              height: '88%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))',
            }}
          />
        </div>
        <img
          src={PORTRAIT_FRAME}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Bars column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 2,
            width: BAR_W,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '2px 6px',
              background: 'linear-gradient(180deg, rgba(20,16,8,0.85), rgba(8,6,4,0.85))',
              border: '1px solid #5a4520',
              boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.15), 0 1px 2px rgba(0,0,0,0.6)',
              color: '#ffe8b3',
              fontFamily: '"Philosopher", "Marcellus", serif',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.4,
              textShadow: '0 1px 2px #000',
              flexShrink: 0,
              height: BAR_H,
              boxSizing: 'border-box',
            }}
          >
            <span>Lv {level}</span>
            {resetCount > 0 && (
              <>
                <span style={{ opacity: 0.5 }}>|</span>
                <span style={{ color: '#ff7a3a' }}>RS {resetCount}</span>
              </>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FramedBar
              pct={expDisplay / safeMaxExp}
              fill="linear-gradient(90deg,#ffd54f,#f57f17)"
              label={
                expMul > 1
                  ? `EXP ${Math.floor((expDisplay / safeMaxExp) * 100)}%  x${expMul.toFixed(1)}`
                  : `EXP ${Math.floor((expDisplay / safeMaxExp) * 100)}%`
              }
              frameSrc={EXP_FRAME}
              width="100%"
              height={BAR_H}
              fontSize={EXP_FONT}
              inset={EXP_INSET}
            />
          </div>
        </div>
        <FramedBar
          pct={hpDisplay / safeMaxHp}
          fill="linear-gradient(90deg,#ff7a7a,#b71c1c)"
          label={`HP ${Math.round(hpDisplay)} / ${safeMaxHp}`}
          frameSrc={HP_FRAME}
          width={BAR_W}
          height={BAR_H}
          fontSize={HP_FONT}
          inset={HP_INSET}
        />
        <FramedBar
          pct={mpDisplay / safeMaxMp}
          fill="linear-gradient(90deg,#5dade2,#1565c0)"
          label={`MP ${Math.round(mpDisplay)} / ${safeMaxMp}`}
          frameSrc={MP_FRAME}
          width={BAR_W}
          height={BAR_H}
          fontSize={MP_FONT}
          inset={MP_INSET}
        />
        <BuffBar />
      </div>
    </div>
  );
}
